import { useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { observer } from 'mobx-react-lite';

import { Badge } from '@components/badge';
import { Button } from '@components/button';
import { InfoBox } from '@components/info-box';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { TextField } from '@components/text-field';
import { useToaster } from '@components/toaster';

import { api } from 'api';
import { Loading } from 'components/Loading';
import { useStore } from 'models/Provider';

export const TimonSettingsPage = observer(() => {
  const [state, setState] = useState({
    url: '',
    username: '',
    password: '',
  });
  const [errorMessage, setErrorMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { t } = useStore();
  const { showSuccess } = useToaster();
  const settingsQuery = useQuery({
    queryKey: ['timonSettings'],
    queryFn: api.integrations.timonGetSettings,
    onSuccess: (data) => {
      setState((old) => ({
        ...old,
        url: data.url,
        username: data.username,
      }));
    },
  });
  function saveSettings() {
    setSubmitting(true);
    api.integrations
      .timonSaveSettings(state.url, state.username, state.password)
      .then(() => {
        showSuccess(t('integrations.timon.settings.save.success'));
        setErrorMessage('');
      })
      .catch((e) => {
        setErrorMessage(e.response.data.reason);
      })
      .finally(() => {
        setSubmitting(false);
      });
  }
  return (
    <Stack space={4}>
      <Stack space={0}>
        <Text font="heading2" text="Timon" />
        {settingsQuery.isSuccess && settingsQuery.data.connected && (
          <Badge variant="green" text={t('integrations.timon.settings.connected')} />
        )}
      </Stack>
      <Separator />
      {(settingsQuery.isLoading || submitting) && <Loading fixed />}
      {settingsQuery.isSuccess && (
        <Stack space={6}>
          <Text text={t('integrations.timon.settings.description')} />
          {errorMessage && <InfoBox variant="red" text={errorMessage} />}
          <TextField
            label={t('integrations.timon.settings.url.label')}
            isDisabled={submitting}
            onChange={(value) => setState((old) => ({ ...old, url: value }))}
            defaultValue={state.url}
          />
          <TextField
            label={t('integrations.timon.settings.username.label')}
            isDisabled={submitting}
            onChange={(value) => setState((old) => ({ ...old, username: value }))}
            defaultValue={state.username}
          />
          <TextField
            label={t('integrations.timon.settings.password.label')}
            isDisabled={submitting}
            onChange={(value) => setState((old) => ({ ...old, password: value }))}
          />
          <Button
            text={t('integrations.timon.settings.submit.label')}
            isDisabled={Object.values(state).some((value) => value === '') || submitting}
            onClick={saveSettings}
          />
        </Stack>
      )}
    </Stack>
  );
});
